import React from 'react';
import { Box, Card, CardActionArea, Chip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { PulseCardData } from '../../store/optionsSlice';
import { SparklineChart } from './SparklineChart';
import FreshnessBadge from '../common/FreshnessBadge';
import { useAppTheme } from '../../hooks/useAppTheme';

function pcrArrow(pcr: number) {
  if (pcr > 1.1) return '↑ Bearish';
  if (pcr < 0.9) return '↓ Bullish';
  return '→ Neutral';
}

function isExchangeOpen(exchange: string): boolean {
  const now = new Date();
  const ist = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const h = ist.getHours(), m = ist.getMinutes();
  const total = h * 60 + m;
  const day = ist.getDay();
  if (exchange === 'DELTA') return true;
  if (day === 0 || day === 6) return false;
  if (exchange === 'MCX') return total >= 540 && total <= 1410;
  return total >= 555 && total <= 930;
}

export type PulseTier = 'hero' | 'standard' | 'compact';

interface Props {
  card: PulseCardData;
  signalScore?: number;
  /** Visual emphasis. 'hero' = top conviction (bigger, accent border).
   *  'standard' = default. 'compact' = small chip-style row (saves space). */
  tier?: PulseTier;
}

const PulseCard: React.FC<Props> = ({ card, signalScore, tier = 'standard' }) => {
  const navigate = useNavigate();
  const { C } = useAppTheme();

  const m = card.latestMetrics;
  const score = Number(signalScore ?? 0);
  const ivRank = m ? Number(m.ivRank52w || 0) : 0;
  const isHero = tier === 'hero';
  const isCompact = tier === 'compact';

  const ivrColor = (ivr: number) => ivr > 65 ? C.bear : ivr > 35 ? C.warn : C.bull;
  const scoreColor = (s: number) => s >= 85 ? C.bull : s >= 70 ? C.warn : C.bear;
  const scoreBg = (s: number) => s >= 85 ? C.bullMuted : s >= 70 ? C.warnMuted : C.bearMuted;

  const badge = score >= 85
    ? { label: 'APPROVE', color: C.bull, bg: C.bullMuted }
    : score >= 70
    ? { label: 'REVIEW',  color: C.warn, bg: C.warnMuted }
    : { label: 'REJECT',  color: C.bear, bg: C.bearMuted };

  const handleClick = () => navigate(`/options-lab/${card.symbol}`);
  const exchangeOpen = isExchangeOpen(card.exchange);
  const spotZero = m && Number(m.spotPrice || 0) === 0;
  const isClosedState = !exchangeOpen && (spotZero || card.dataStale);

  // Compact tier — single-row layout used for low-conviction symbols. Saves
  // vertical space on the dashboard once the conviction list is long.
  if (isCompact && !isClosedState) {
    const sigDir = card.latestSignal?.direction;
    return (
      <Card
        sx={{
          minWidth: 200,
          maxWidth: 240,
          flexShrink: 0,
          border: '1px solid',
          borderColor: card.dataStale ? C.bearBorder : 'divider',
          borderRadius: 2,
        }}
      >
        <CardActionArea onClick={handleClick} sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.1 }} noWrap>{card.symbol}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.62rem' }}>
              {m ? `₹${Number(m.spotPrice || 0).toLocaleString('en-IN')}` : '—'} · IVR {ivRank.toFixed(0)}
            </Typography>
          </Box>
          {signalScore != null && (
            <Chip
              size="small"
              label={score.toFixed(0)}
              sx={{ bgcolor: scoreBg(score), color: scoreColor(score), fontWeight: 800, fontSize: '0.7rem', height: 22, minWidth: 36 }}
            />
          )}
          {sigDir && (
            <Chip
              size="small"
              label={sigDir === 'LONG' ? 'L' : 'S'}
              sx={{ bgcolor: sigDir === 'LONG' ? C.bullMuted : C.bearMuted, color: sigDir === 'LONG' ? C.bull : C.bear, fontWeight: 700, height: 22, minWidth: 22 }}
            />
          )}
        </CardActionArea>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        minWidth: isHero ? 300 : 260,
        maxWidth: isHero ? 360 : 300,
        flexShrink: 0,
        border: isHero ? '2px solid' : '1px solid',
        borderColor: card.dataStale ? C.bearBorder : (isHero ? C.accentBorder : 'divider'),
        borderRadius: 2,
        position: 'relative',
        transition: 'all 0.2s',
        boxShadow: isHero ? 3 : 0,
        '&:hover': { boxShadow: 4, borderColor: C.neutral }
      }}
    >
      {isHero && (
        <Box sx={{
          position: 'absolute', top: -10, left: 10, zIndex: 1,
          px: 0.75, py: 0.1, borderRadius: 0.5,
          bgcolor: C.accent, color: C.onSolid, fontSize: '0.6rem', fontWeight: 800, letterSpacing: 0.5,
        }}>
          ★ HIGH CONVICTION
        </Box>
      )}
      <CardActionArea onClick={handleClick} sx={{ p: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>{card.symbol}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {card.exchange} · {card.isIndex ? 'INDEX' : 'STOCK'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            {/* Market Context chip */}
            <Chip size="small"
              label={isClosedState ? `${card.exchange} CLOSED` : card.dataStale ? 'LOW VOLUME' : 'TRADING'}
              sx={{
                bgcolor: isClosedState ? C.dimMuted : card.dataStale ? C.warnMuted : C.bullMuted,
                color:   isClosedState ? C.dim      : card.dataStale ? C.warn      : C.bull,
                fontWeight: 700, fontSize: '0.58rem', height: 18
              }}
            />
            {/* IVR Badge */}
            {m && m.ivRank52w != null && !isClosedState && (
              <Chip size="small" label={`IVR ${ivRank.toFixed(0)}`}
                sx={{ bgcolor: `${ivrColor(ivRank)}22`, color: ivrColor(ivRank), fontWeight: 700, fontSize: '0.7rem' }} />
            )}
          </Box>
        </Box>

        {isClosedState ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="body2" sx={{ color: C.dim, fontWeight: 700, mb: 0.5 }}>
              {card.exchange} CLOSED
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {card.exchange === 'MCX' ? 'MCX opens Mon-Fri 9:00 AM IST' :
               card.exchange === 'DELTA' ? '' : 'NSE/NFO opens Mon-Fri 9:15 AM IST'}
            </Typography>
            {m && m.computedAt && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                Last data: {new Date(m.computedAt).toLocaleString('en-IN')}
              </Typography>
            )}
          </Box>
        ) : (
          <>
            {/* One-line verdict */}
            {m && (
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 1, fontSize: '0.68rem', fontStyle: 'italic', lineHeight: 1.4 }}>
                {(() => {
                  const pcr = Number(m.pcrOi || 1);
                  const gex = Number(m.gexTotal || 0);
                  const bias = pcr > 1.1 ? 'Bearish bias' : pcr < 0.9 ? 'Bullish bias' : 'Neutral';
                  const ivState = ivRank > 65 ? 'IV expensive' : ivRank < 30 ? 'IV cheap' : 'IV moderate';
                  const gexState = gex < -500000 ? 'Neg GEX amplifies' : gex > 500000 ? 'Pos GEX pins' : 'GEX neutral';
                  const strategy = ivRank > 65 ? 'Credit spreads preferred' : ivRank < 30 ? 'Debit spreads preferred' : 'Defined-risk structures';
                  return `${bias}. ${ivState}. ${gexState}. ${strategy}.`;
                })()}
              </Typography>
            )}

            {/* Spot price */}
            {m && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1 }}>
                  ₹{Number(m.spotPrice || 0).toLocaleString('en-IN')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Spot Price</Typography>
              </Box>
            )}

            {/* Key metrics grid */}
            {m && (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.5 }}>
                <MetricCell label="ATM IV" value={Number(m.atmIv || 0) === 0 && !exchangeOpen ? '—' : `${Number(m.atmIv || 0).toFixed(1)}%`} color={ivrColor(ivRank || 50)} />
                <MetricCell label="PCR-OI" value={`${Number(m.pcrOi || 0).toFixed(2)} ${pcrArrow(m.pcrOi || 1)}`} />
                <MetricCell label="Straddle" value={m.straddlePrice ? `₹${Number(m.straddlePrice).toFixed(0)}` : '—'} />
                <MetricCell label="Max Pain" value={m.maxPainStrike ? `₹${Number(m.maxPainStrike).toLocaleString('en-IN')}` : '—'} color={C.maxPain} />
              </Box>
            )}

            {/* Regime tags */}
            {m && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                <RegimeTag label={ivRank > 65 ? 'HIGH IV' : ivRank < 35 ? 'LOW IV' : 'MID IV'} color={ivrColor(ivRank || 50)} />
                <RegimeTag
                  label={Number(m.pcrOi) > 1.1 ? 'BEARISH OI' : Number(m.pcrOi) < 0.9 ? 'BULLISH OI' : 'NEUTRAL OI'}
                  color={Number(m.pcrOi) > 1.1 ? C.bear : Number(m.pcrOi) < 0.9 ? C.bull : C.warn} />
                <RegimeTag
                  label={Number(m.gexTotal || 0) < 0 ? 'NEG GEX' : 'POS GEX'}
                  color={Number(m.gexTotal || 0) < 0 ? C.warn : C.bull} />
              </Box>
            )}

            {/* Confluence score */}
            {signalScore != null && (
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>CONFLUENCE</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: scoreColor(score) }}>{score.toFixed(0)}</Typography>
                </Box>
                <Box sx={{ height: 4, bgcolor: 'action.hover', borderRadius: 2 }}>
                  <Box sx={{ height: '100%', width: `${Math.min(score, 100)}%`, bgcolor: scoreColor(score), borderRadius: 2, transition: 'width 0.4s' }} />
                </Box>
              </Box>
            )}

            {/* Decision badge */}
            {signalScore != null && (
              <Chip size="small" label={badge.label}
                sx={{ width: '100%', bgcolor: badge.bg, color: badge.color, fontWeight: 800, letterSpacing: 1, borderRadius: 1 }} />
            )}

            {/* Last signal */}
            {card.latestSignal && (
              <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.62rem' }}>
                  Last signal: <strong style={{ color: card.latestSignal.direction === 'LONG' ? C.bull : C.bear }}>
                    {card.latestSignal.direction}
                  </strong> {Number(card.latestSignal.score).toFixed(0)} {card.latestSignal.regimeTag} — {(() => {
                    const mins = Math.floor((Date.now() - new Date(card.latestSignal!.createdAt).getTime()) / 60000);
                    return mins < 60 ? `${mins}m ago` : `${Math.floor(mins / 60)}h ago`;
                  })()}
                </Typography>
              </Box>
            )}

            {/* Freshness badge */}
            {m && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <FreshnessBadge
                  computedAt={m.computedAt}
                  isExchangeOpen={exchangeOpen}
                  feedSource={m.feedSource}
                  size="small"
                />
              </Box>
            )}
          </>
        )}
      </CardActionArea>
    </Card>
  );
};

const MetricCell: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color }) => (
  <Box>
    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem', color: color || 'text.primary' }}>
      {value}
    </Typography>
  </Box>
);

const RegimeTag: React.FC<{ label: string; color: string }> = ({ label, color }) => (
  <Box sx={{
    px: 0.75, py: 0.25,
    bgcolor: `${color}22`,
    color,
    borderRadius: 0.75,
    fontSize: '0.62rem',
    fontWeight: 700,
    letterSpacing: 0.3
  }}>
    {label}
  </Box>
);

export default PulseCard;
